import { ActivatedRoute, Params, Router } from '@angular/router';
import { AfterViewInit, Component, ElementRef, HostListener, OnDestroy, OnInit, QueryList, TemplateRef, ViewChild, ViewChildren } from '@angular/core';
import { ToastsService } from '@app/shared/services/toasts.service';
import { AndroWebCoreComponent } from '@app/core/AndroWebCoreComponent';
import { Basket } from '@app/models/basket';
import { BasketService } from '@app/api/basket.service';
import { CheckoutService } from '@app/api/checkout.service';
import { DisplayOccasionType } from '@app/models/display-occasion-type';
import { MenuService } from '@app/api/menu.service';
import { NavigationSectionType } from '@app/models/navigation/navigation-section-type';
import { NavigatorService } from '@app/core/navigator.service';
import { OrderOccasion } from '@app/models/order-occasion';
import { PageSEO } from '@app/models/page-seo';
import { SEOService } from '@app/shared/services/seo.service';
import { SeverityLevel } from '@microsoft/applicationinsights-web';
import { Site } from '@app/models/site';
import { SiteService } from '@app/api/site.service';
import { DatePipe } from '@angular/common';
import { BasketItem } from '@app/models/basket/basket-item';
import { ChargeTypes } from '@app/models/charge-types';
import { MatDialogConfig } from '@angular/material/dialog';
import { Charge } from '@app/models/Charge';
import { IDictionary } from '@app/models/IDictionary';
import { Observable, lastValueFrom, takeUntil } from 'rxjs';
import { filter } from 'rxjs/operators';
import { trigger, transition, style, animate } from '@angular/animations';
import { INotification } from '@app/models/INotification';
import { ToastTypes } from '@app/models/ToastTypes.enum';
import { IDaysTimeSlots } from '@app/models/wanted-time-picker/IDaysTimeSlots';
import { BasketAvailableTime } from '@app/models/basket-available-time';
import { Address } from '@app/models/Address';
import { MenuItemView } from '@app/models/menu/menu-item-view';
import { MenuSectionView } from '@app/models/menu/menu-section-view';
import { MenuHelperService } from '@app/shared/services/menu-helper/menu-helper.service';
import { Deal } from '@app/models/menu/deal';
import { Product } from '@app/models/menu/product';
import { MenuSection } from '@app/models/menu/menu-section';
import { Menu } from '@app/models/menu/menu';
import { MenuDealHelperService } from '@app/shared/services/menu-deal-helper/menu-deal-helper.service';
import { MenuVariantHelperService } from '@app/shared/services/menu-variant-helper/menu-variant-helper.service';
import { StructuredDataService } from '@app/shared/services/structured-data/structured-data.service';
import { getWantedTimeOrLeadTime } from '@app/shared/utils/date-utils';
import { ProductAddEvent } from '@app/models/product/product-add-event';
import { ProductUpsellModalComponent } from './components/product-upsell-modal/product-upsell-modal.component';
import { ProductUpsellModalData } from '@app/models/product/product-upsell-modal-data';

@Component({
  animations: [
    trigger('valueAnimation', [
      transition(':increment', [
        style({ fontSize: '22px' }),
        animate('0.5s ease', style('*'))
      ]
      ),
      transition(':decrement', [
        style({ fontSize: '22px' }),
        animate('0.5s ease', style('*'))
      ]
      )
    ])
  ],
  selector: 'app-menu',
  styleUrls: ['./menu.component.scss'],
  templateUrl: './menu.component.html'
})
export class MenuComponent extends AndroWebCoreComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChildren('categoryElement') private _categoryElements: QueryList<ElementRef>;
  @ViewChild('search') private _searchElement: ElementRef;
  @ViewChild('basketModalContent') private basketModalContent: TemplateRef<any>;

  public siteId: string;
  public searchText: string;
  public occasion: OrderOccasion;
  public highlightedCategoryName: string;
  public noMenu: boolean;
  public showSearchBarExpanded: boolean;
  public isLoading: boolean = true;
  public isStoreOpen: boolean;
  public navigationSectionType: typeof NavigationSectionType = NavigationSectionType;
  public basketWantedTime: string;
  public basket: Basket;
  public basketTotal: number;
  public notifications: IDictionary<Observable<INotification>>;
  public currentBasketCount: number;
  public currentSite: Site;
  public orderOccasion: typeof OrderOccasion = OrderOccasion;
  public sections: MenuSectionView[];
  public showWebSectionImages: boolean;

  private _location: string;
  private _seoSet: boolean;
  private _scrollDelay: number = 200;

  constructor(
    public router: Router,
    private _datePipe: DatePipe,
    private _seoService: SEOService,
    private _menuService: MenuService,
    private _siteService: SiteService,
    private _toastService: ToastsService,
    private _basketService: BasketService,
    private _activatedRoute: ActivatedRoute,
    private _checkoutService: CheckoutService,
    private _navigatorService: NavigatorService,
    private _menuHelperService: MenuHelperService,
    private _menuDealHelperService: MenuDealHelperService,
    private _structuredDataService: StructuredDataService,
    private _menuVariantHelperService: MenuVariantHelperService
  ) {
    super();
    this.showWebSectionImages = this.tenant.SuppressProductImages;
  }

  ngOnInit() {
    this.handleRouteParams();
    this.updatePageSEO(false);
    this.trackCurrentSite();
    this.getCurrentBasketBySiteId();
    this.trackCurrentMenu();
    this.trackCurrentBasket();
    this.trackNotifications();
  }

  ngAfterViewInit() {
    this._activatedRoute.queryParams
        .pipe(
            filter((params: Params) => params.openBasket),
            takeUntil(this.destroy$)
        )
        .subscribe(() => {
          this.openBasket(this.basketModalContent);
        });
  }

  ngOnDestroy() {
    super.ngOnDestroy();

    if (this._seoSet) {
      this._seoService.restore();
    }
  }

  /**
   * updates which category (web section) is highlighted whilst the user scrolls
   * @param $event
   */
  @HostListener('window:scroll')
  public highlightCategoryName(): void {
    const visibleElement: ElementRef = this.getCategoryInView();
    const name: string = visibleElement ? visibleElement.nativeElement.id : '';

    if (this.highlightedCategoryName !== name) {
      this.highlightedCategoryName = name;
      this.scrollSectionButtonInToView(name);
    }
  }

  /**
   * sets showSearchBarExpanded to true
   */
  public toggleSearchBar(): void {
    this.showSearchBarExpanded = !this.showSearchBarExpanded;

    if (this.showSearchBarExpanded) {
      this.setSearchFieldFocus();
    } else {
      this.searchText = '';
    }
  }

  /**
   * navigates to the displayGroup with the matching name
   * @param $event
   * @param menuSection
   */
  public navigateToDisplayGroup($event: MouseEvent, menuSection: MenuSectionView): void {
    $event.preventDefault();
    const customerName: string = this._navigatorService.clean(menuSection.name);
    this.router.navigate([this.getSectionUrl(customerName)]);
    this.highlightedCategoryName = customerName;
    this.scrollSectionButtonInToView(customerName);
    this.scrollToWebSection(customerName);
    this._seoService.updateSeoTitleAndDescription(this.currentSite, this.occasion, menuSection);
  }

  /**
   * opens `https://andromedapos.com/` in a new tab
   */
  public navigateToAndromeda(): void {
    window.open('https://andromedapos.com/');
  }

  /**
  * opens the order wanted time modal
  */
  public openWantedTimeModal(modal: TemplateRef<any>): void {
    this.openDialog(modal, 'order-wanted-time-modal', { width: '' });
  }

  /**
   * opens a mat dialog with the basket template inside
   */
  public openBasket(template: TemplateRef<any>): void {
    const openBasketModal = () => {
      const options: MatDialogConfig = {
        height: this.isTablet ? '100%' : '90%',
        panelClass: 'basket-modal-window',
        position: {
          right: this.isTablet ? '0px' : '35px',
          top: this.isTablet ? '0' : (this.isShortDevice ? '45px' : '75px')
        },
        width: this.isTablet ? '100%' : '580px'
      };

      this.openDialog(template, 'basket-modal', options);
    };

    const wantedTime: Date = getWantedTimeOrLeadTime(this.occasion, this.currentSite, this.basket);
    const upselling = this._menuVariantHelperService.getBasketUpsellOptions(this.basket.Upselling, this._menuService.menuValue, this.occasion, wantedTime);

    if (upselling?.options.length > 0) {
      const options: Partial<MatDialogConfig<ProductUpsellModalData>> = {
        data: {
          options: upselling.options,
          occasion: this.basket.Occasion,
          save: (event: ProductAddEvent, name: string) => this._basketService.addProductToBasket(event.item, name, 'basket-upsell', event.itemPrice),
          prompt: upselling.prompt
        }
      };

      this.openDialog<any, ProductUpsellModalData>(ProductUpsellModalComponent, 'upsell-modal', options)
          .afterClosed()
          .pipe(takeUntil(this.destroy$))
          .subscribe(() => openBasketModal());
      return;
    }

    openBasketModal();
  }

  /**
   * tracks the current menu.
   */
  private trackCurrentMenu(): void {
    let menuUpdates: number = 0;

    this._menuService.menu$
        .pipe(takeUntil(this.destroy$))
        .subscribe((menu: Menu) => {
          this.noMenu = !menu;

          menuUpdates++;

          if (this.noMenu) {
            if (menuUpdates > 1) {
              this.isLoading = false;
            }

            return;
          }

          this.sections = this.getMenuSections(menu);
          this.buildStructuredData(menu);
          this.updatePageSEO(true);
          this.isLoading = false;
        });
  }

  /**
   * returns the menu sections for the given menu
   * @param menu
   */
  private getMenuSections(menu: Menu): MenuSectionView[] {
    return this._menuHelperService.getProductSections(menu, this.occasion).map((x: MenuSection) => this.mapSectionToMenuSectionView(x, menu));
  }

  /**
   * maps a MenuSection to a MenuSectionView
   * @param section
   * @param menu
   */
  private mapSectionToMenuSectionView(section: MenuSection, menu: Menu): MenuSectionView {
    const cleanName: string = this._navigatorService.clean(section.Name);

    return {
      name: section.Name,
      cleanName,
      description: section.Description,
      id: section.Id,
      menuItems: this.mapToMenuItemView(section, menu),
      imageBase: this.getMenuImageVisualAsset({ data: section, height: this.isMobile ? 99 : 185 }),
      href: this.getSectionUrl(cleanName)
    };
  }

  /**
   * returns the url for the given section
   * @param sectionName - needs to be a clean name {@link NavigatorService.clean}
   */
  private getSectionUrl(sectionName: string): string {
    return this._navigatorService.generateRoutePath(this._location, this.occasion, NavigationSectionType.Menu, sectionName);
  }

  /**
   * maps the products and deals to a MenuItemView
   */
  private mapToMenuItemView(section: MenuSection, menu: Menu): MenuItemView[] {
    const wantedTime: Date = getWantedTimeOrLeadTime(this.occasion, this.currentSite, this.basket);

    const products: MenuItemView[] = this._menuHelperService.getProductsForSectionAndOccasion(section, OrderOccasion[this.occasion], menu.Products)
        .map((product: Product) => this.mapProductToMenuItemView(product, wantedTime));

    const deals: MenuItemView[] = this._menuHelperService.getDealsForSectionAndOccasion(section, OrderOccasion[this.occasion], menu.Deals)
        .filter((x: Deal) => !x.RequiresUnlock)
        .map((deal: Deal) => this.mapDealToMenuItemView(deal, wantedTime));

    return [...products, ...deals]
        .filter((i: MenuItemView) => i.available)
        .sort((a: MenuItemView, b: MenuItemView) => a.name.localeCompare(b.name))
        .sort((a: MenuItemView, b: MenuItemView) => (a.sequence ?? 0) - (b.sequence ?? 0));
  }

  /**
   * maps a product to a MenuItemView
   * @param product
   * @param wantedTime
   */
  private mapProductToMenuItemView(product: Product, wantedTime: Date): MenuItemView {
    const priceSummary = this._menuVariantHelperService.getPriceAndSummaryForVariants(product.Variants, OrderOccasion[this.occasion]);

    return {
      id: product.Id,
      outOfStock: product.Variants.every((variant) => variant.OutOfStock),
      imageBase: this.getMenuImageVisualAsset({ data: product, height: this.isMobile ? 99 : 185 }),
      sequence: product.Sequence,
      name: product.Name,
      description: product.Description,
      nutritionSummary: this._menuVariantHelperService.getNutritionSummary(product.Variants, false),
      available: this._menuVariantHelperService.areVariantsAvailableForOccasion(product.Variants, OrderOccasion[this.occasion], wantedTime),
      priceSummary: priceSummary.summary,
      price: priceSummary.price,
      type: 'product',
      defaultVariantSummary: this._menuHelperService.getVariantOptionsPathText(
          this._menuVariantHelperService.getDefaultVariant(product.Variants),
          this._menuService.productOptionsById
      ),
      variants: product.Variants,
      upselling: product.Upselling
    };
  }

  /**
   * maps a deal to a MenuItemView
   */
  private mapDealToMenuItemView(deal: Deal, wantedTime: Date): MenuItemView {
    const price = this._menuDealHelperService.getMinPriceForDeal(deal, OrderOccasion[this.occasion]);
    return {
      id: deal.Id,
      outOfStock: deal.OutOfStock,
      imageBase: this.getMenuImageVisualAsset({ data: deal, height: this.isMobile ? 99 : 185 }),
      sequence: deal.Sequence,
      name: deal.Name,
      description: deal.Description,
      available: this._menuHelperService.availableForOccasion(deal.Availability?.Values, OrderOccasion[this.occasion], wantedTime),
      priceSummary: this._menuDealHelperService.getPricePrefixForDeal(deal) + price.toFixed(2),
      price,
      type: 'deal'
    };
  }

  /**
   * scroll into view of the given group
   * @param groupName
   */
  private scrollSectionButtonInToView(groupName: string): void {
    const element: HTMLElement = document.getElementById(`li-${groupName}`);

    if (element) {
      setTimeout(() => {
        element.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
      }, 100);
    }
  }

  /**
   * tracks the basket total.
   * @param basket
   */
  private trackBasketCount(basket: Basket): void {
    if (basket) {
      let total: number = basket.Deals?.length ?? 0;
      basket.Items?.forEach((item: BasketItem) => total += item.Product.Quantity);

      if (this.currentBasketCount !== total) {
        this.currentBasketCount = total;
      }
    }
  }

  /**
   * returns the category (web-section) that is in the top 50% of the users view.
   */
  private getCategoryInView(): ElementRef<any> {
    const passedElements: ElementRef[] = [];

    this._categoryElements.forEach((element: ElementRef) => {
      const midWayOfScreen: number = document.body.clientHeight / 2;

      if (element.nativeElement.getBoundingClientRect().top < midWayOfScreen) {
        passedElements.push(element);
      }
    });

    return passedElements.pop();
  }

  /**
   * Pulls out the siteId, groupFilter and returnUrl from the parameters in the current url if any are present
   */
  private handleRouteParams(): void {
    if (this._activatedRoute.snapshot.paramMap.get('occasion')) {
      const occasion = this._navigatorService.changeTakeAwayToCollection(this._activatedRoute.snapshot.paramMap.get('occasion'));
      const capitalizedOccasion = occasion.charAt(0).toUpperCase() + occasion.slice(1);
      this.occasion = DisplayOccasionType[capitalizedOccasion];

      // if we don't know the occasion type, redirect home so the user can select
      if (!this.occasion) {
        this.trackTrace('Unknown occasion type, user redirected to home', SeverityLevel.Warning);
        this.router.navigate([this._navigatorService.generateHomePath()]);
      }
    }

    if (!this._activatedRoute.snapshot.paramMap.get('displayGroup')) {
      this.router.navigate([`${decodeURI(location.pathname)}/all`]);
    }

    if (this._activatedRoute.snapshot.paramMap.get('location')) {
      this._location = this._activatedRoute.snapshot.paramMap.get('location');
      this.siteId = this._navigatorService.getSiteIdByLocation(this._location);
    }

    if (!this.siteId || this.siteId === '') {
      this.router.navigate([this._navigatorService.generateHomePath()]);
    }
  }

  /**
   * builds the structured data for the menu
   */
  private buildStructuredData(menu: Menu): void {
    const jsonStructuredData: string = this._structuredDataService.buildMenuStructuredData(menu, this.occasion);

    if (jsonStructuredData) {
      const jsonScript: HTMLScriptElement = document.createElement('script');
      jsonScript.setAttribute('type', 'application/ld+json');
      jsonScript.textContent = jsonStructuredData;
      document.head.appendChild(jsonScript);
    }
  }

  /**
   * updates the websites title and meta description
   * @param useDefault
   */
  private updatePageSEO(useDefault: boolean): void {
    if (useDefault) {
      this._seoSet = true;
      if (this.currentSite && this.occasion) {
        this._seoService.updateSeoTitleAndDescription(this.currentSite, this.occasion);
      } else {
        this._seoService.updateTitle('{{TenantWebsiteName}} - menu');
      }

      const address: Address = this.currentSite?.Address;
      const locationDetails: string = address
        ? `${address.Town ? `in ${address.Town} ` : ''} ${(address.Town || !address.Postcode) ? address.Postcode : `in ${address.Postcode}`}`
        : '';

      this._seoService.updateDescription(this.replaceSEOStrings(`See our ${ this._navigatorService.changeCollectionToTakeAway(this.occasion) } menu in ${locationDetails}.
                      Choose from our wide range of {{allMenuCategories}} from ${this.currentSite?.Name}.`));
    } else {
      const menuPageSEO = this.tenant.PageSeos.find((seo: PageSEO) => seo.PageName.toLowerCase() === 'menu');

      if (menuPageSEO) {
        this._seoSet = true;
        this._seoService.updateTitle(this.replaceSEOStrings(menuPageSEO.Title));
        this._seoService.updateDescription(this.replaceSEOStrings(menuPageSEO.Description));
      } else {
        this.updatePageSEO(true);
      }
    }
  }

  /**
   * replaces well defined hardcoded strings with matching details for the current site/menu
   * @param updateText
   */
  private replaceSEOStrings(updateText: string): string {
    if (updateText.includes('{{allMenuCategories}}') && this.sections?.length > 0) {
      return updateText.replace('{{allMenuCategories}}', this.sections.map((x: MenuSectionView) => x.name).join(', '));
    }

    return updateText;
  }

  /**
   * tracks the current site.
   */
  private trackCurrentSite(): void {
    this._siteService.currentSite$
        .pipe(takeUntil(this.destroy$))
        .subscribe((site: Site) => {
          this.currentSite = site;
        });

    if (!this.currentSite || this.currentSite.Id.toLocaleLowerCase() !== this.siteId.toLocaleLowerCase()) {
      this._siteService.setCurrentSiteBySiteId(this.siteId);
    }
  }

  /**
   * gets the current basket
   * @param retried
   */
  private getCurrentBasketBySiteId(): void {
    const clearOldBasket = () => this._basketService.clearOldAndCreateNewBasket(this.siteId);

    this._basketService.getCurrentBasketBySiteId(this.siteId, OrderOccasion[this.occasion])
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          error: (error) => {
            this.trackException(error, true);
            this.goHome();
          },
          next: async (basket: Basket) => {
            if (!basket) {
              this.trackTrace('getCurrentBasketBySiteId returned null on menu page', SeverityLevel.Error);
              this.goHome();
              return;
            }

            if (basket.IsModifiable) {
              return;
            }

            if (!basket.OrderId) {
              await clearOldBasket();
              return;
            }

            this.basket = null;
            await this._checkoutService.deleteOrderById(basket.OrderId).catch(async () => await clearOldBasket());
            this.getCurrentBasketBySiteId();
          }
        });
  }

  /**
   * tracks the current basket
   */
  private trackCurrentBasket(): void {
    this._basketService.currentBasket$
        .pipe(takeUntil(this.destroy$))
        .subscribe((basket: Basket) => {
          const isNewBasket: boolean = !this.basket || !basket || this.basket.Id !== basket.Id;

          this.basket = basket;

          if (!basket) {
            return;
          }

          if (isNewBasket) {
            this.getAvailableOrderDates();
          }

          this.trackBasketCount(basket);
          this.basketWantedTime = this.getWantedTime();
          this.basketTotal = this.basket.Charges?.find((x: Charge) => x.ChargeType === ChargeTypes.Total)?.Price.Amount ?? 0;

          if (this._menuService.menuValue) {
            this.sections = this.getMenuSections(this._menuService.menuValue);
          }
        });
  }

  /**
   * tracks the active notifications
   */
  private trackNotifications(): void {
    this._toastService.activeNotifications$
        .pipe(takeUntil(this.destroy$))
        .subscribe((x: IDictionary<Observable<INotification>>) => {
          this.notifications = x;
        });
  }

  /**
   * returns the user to the home page.
   */
  private goHome(): void {
    if (!this.router.url.includes('/menu')) {
      return;
    }

    this._toastService.showToast(ToastTypes.error, 'Something\'s gone wrong');
    this.router.navigate([this._navigatorService.generateHomePath()]);
  }

  /**
   * scrolls to the given web section (`groupName`)
   */
  private scrollToWebSection(groupName: string): void {
    const element: HTMLElement = document.getElementById(groupName);

    if (!element) {
      return;
    }

    if (this.isMobile) {
      setTimeout(() => window.scrollTo({ behavior: 'smooth', top: element.offsetTop }), this._scrollDelay);
      return;
    }

    const parent: HTMLElement = document.getElementById('menu-content');
    const catBar: HTMLElement = document.getElementById('categories-bar');
    parent.scrollTo({ behavior: 'smooth', top: element.offsetTop - catBar.offsetHeight });
  }

  /**
   * sets a focus to a search filed
   */
  private setSearchFieldFocus(): void {
    const searchFocusDelay: number = 10;
    setTimeout(() => this._searchElement.nativeElement.focus(), searchFocusDelay);
  }

  /**
   *  returns the baskets wanted time formatted as HH:mm or 'ASAP'.
   */
  private getWantedTime(): string {
    if (this.basket.WantedTimeUtc) {
      const _date = new Date(this.replaceZuluTime(this.basket.WantedTimeUtc));
      return this._datePipe.transform(_date, this.isToday(_date) ? 'HH:mm' : 'EEE dd/MM HH:mm');
    } else {
      return 'ASAP';
    }
  }

  private isToday(date: Date): boolean {
    const today = new Date();
    return date.getDate() === today.getDate() && date.getMonth() === today.getMonth();
  }

  /**
   * updates the `IsStoreOpen` property
   */
  private async getAvailableOrderDates(): Promise<void> {
    const dates: IDaysTimeSlots = await this._basketService.getTimeSlotsForBasket(this.basket.Id, true);
    this.isStoreOpen = dates.Dates?.length > 0 || dates.AllowAsap;

    if (!this.isStoreOpen) {
      const nextDate = new Date();
      nextDate.setDate(nextDate.getDate() + 1);
      const times: BasketAvailableTime[] = await lastValueFrom(this._basketService.getBasketsAvailableTimes(this.basket.Id, nextDate, true));
      const hasTimes = times.flatMap((x: BasketAvailableTime) => x.Time).filter((x: string) => x < '06:00:00').length > 0;

      if (hasTimes) {
        this.isStoreOpen = true;
      }
    }
  }
}
